import type { SimulationActivity } from '../../types/simulation';

function toNonNegativeCount(value: unknown): number | null {
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : NaN;
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  return Math.floor(n);
}

export type ActivityStopKind = 'pick' | 'drop' | 'other';

function getCountFromCandidateFields(
  raw: Record<string, unknown>,
  fieldNames: string[]
): number | null {
  for (const fieldName of fieldNames) {
    const count = toNonNegativeCount(raw[fieldName]);
    if (count !== null) {
      return count;
    }
  }
  return null;
}

function getTripLegCounts(trips: unknown): { pick: number; drop: number } {
  if (!Array.isArray(trips) || trips.length === 0) {
    return { pick: 0, drop: 0 };
  }
  let pick = 0;
  let drop = 0;
  for (const trip of trips) {
    if (!trip || typeof trip !== 'object') continue;
    const at = ((trip as Record<string, unknown>).activity_type ?? '').toString().toUpperCase();
    if (at === 'PICK') pick += 1;
    if (at === 'DROP') drop += 1;
  }
  return { pick, drop };
}

function getPassengerCountFromStandardFields(raw: Record<string, unknown>): number | null {
  const standardCount = toNonNegativeCount(raw.standard_passengers ?? raw.standard_count);
  const wheelchairCount = toNonNegativeCount(
    raw.wheelchair_passengers ?? raw.wheelchair_count
  );
  if (standardCount !== null || wheelchairCount !== null) {
    return (standardCount ?? 0) + (wheelchairCount ?? 0);
  }
  return toNonNegativeCount(raw.passengers_count ?? raw.number_of_passengers);
}

function inferKindFromTrips(trips: unknown): ActivityStopKind | null {
  if (!Array.isArray(trips) || trips.length === 0) return null;
  let hasPick = false;
  let hasDrop = false;
  for (const trip of trips) {
    if (!trip || typeof trip !== 'object') continue;
    const at = ((trip as Record<string, unknown>).activity_type ?? '').toString().toUpperCase();
    if (at === 'PICK') hasPick = true;
    if (at === 'DROP') hasDrop = true;
  }
  if (hasPick && !hasDrop) return 'pick';
  if (hasDrop && !hasPick) return 'drop';
  if (hasPick && hasDrop) return 'pick';
  return null;
}

/**
 * True when `trips` is non-empty and no nested leg is PICK or DROP (e.g. only START/END).
 * Used to ignore spurious top-level pickup/drop counts on depot/shift boundary rows.
 */
function tripsArrayHasNoPickOrDrop(trips: unknown): boolean {
  if (!Array.isArray(trips) || trips.length === 0) return false;
  for (const trip of trips) {
    if (!trip || typeof trip !== 'object') continue;
    const at = ((trip as Record<string, unknown>).activity_type ?? '').toString().toUpperCase().trim();
    if (at === 'PICK' || at === 'DROP') return false;
  }
  return true;
}

/** Top-level activity types that are never passenger pick/drop stops (unless nested trips already set PICK/DROP). */
function isNonPassengerActivityTypeLabel(t: string): boolean {
  const s = t.trim().toLowerCase();
  if (!s) return false;
  if (s === 'start' || s === 'end') return true;
  if (s === 'shift_start' || s === 'shift_end' || s === 'service_start' || s === 'service_end') return true;
  if (s.includes('break')) return true;
  if (s.includes('depot')) return true;
  return false;
}

/**
 * Infer whether a simulation activity is primarily a pickup, drop-off, or neither,
 * using the same signals as the operations UI: nested **trips** (PICK/DROP) when present,
 * ride counts, user id arrays, then activity_type / type strings.
 */
export function inferActivityStopKind(a: SimulationActivity): ActivityStopKind {
  const raw = a as Record<string, unknown>;
  const fromTrips = inferKindFromTrips(raw.trips);
  if (fromTrips) return fromTrips;

  // Depot / service start rows often include nested START-only trips but spurious non-zero pickup counts.
  if (tripsArrayHasNoPickOrDrop(raw.trips)) {
    return 'other';
  }

  const pickCount = toNonNegativeCount(raw.number_of_rides_picked_up);
  const dropCount = toNonNegativeCount(raw.number_of_rides_dropped_off);
  const picked = raw.users_picked_up;
  const dropped = raw.users_dropped_off;
  const pickUsersLen = Array.isArray(picked) ? picked.length : 0;
  const dropUsersLen = Array.isArray(dropped) ? dropped.length : 0;
  const hasPick = (pickCount !== null && pickCount > 0) || pickUsersLen > 0;
  const hasDrop = (dropCount !== null && dropCount > 0) || dropUsersLen > 0;
  if (hasPick && !hasDrop) return 'pick';
  if (hasDrop && !hasPick) return 'drop';
  if (hasPick && hasDrop) return 'pick';

  const t = (a.activity_type ?? raw.type ?? '').toString().toLowerCase();
  if (isNonPassengerActivityTypeLabel(t)) return 'other';

  if (t.includes('pick') || t.includes('origin') || t === 'pickup') return 'pick';
  if (
    t.includes('drop') ||
    t.includes('dest') ||
    t === 'dropoff' ||
    t === 'drop_off' ||
    t === 'delivery' ||
    t === 'destination' ||
    t === 'off'
  ) {
    return 'drop';
  }
  return 'other';
}

/**
 * Infer net passenger delta for a stop.
 * Positive = pickup, negative = dropoff.
 * Uses explicit pick/drop counts first; falls back to inferred stop kind (+/-1).
 */
export function inferActivityPassengerDelta(a: SimulationActivity): number | null {
  const raw = a as Record<string, unknown>;
  const inferredKind = inferActivityStopKind(a);
  const pickCountFields = [
    'number_of_rides_picked_up',
    'pick_count',
    'pickup_count',
    'pickup_passengers_count',
    'picked_up_passengers_count',
    'passengers_picked_up_count',
    'number_of_passengers_picked_up',
  ];
  const dropCountFields = [
    'number_of_rides_dropped_off',
    'drop_count',
    'dropoff_count',
    'drop_off_count',
    'dropoff_passengers_count',
    'passengers_dropped_off_count',
    'number_of_passengers_dropped_off',
  ];

  let pickCount = getCountFromCandidateFields(raw, pickCountFields);
  let dropCount = getCountFromCandidateFields(raw, dropCountFields);

  if (pickCount === null && Array.isArray(raw.users_picked_up)) {
    pickCount = raw.users_picked_up.length;
  }
  if (dropCount === null && Array.isArray(raw.users_dropped_off)) {
    dropCount = raw.users_dropped_off.length;
  }

  if (pickCount === null || dropCount === null) {
    const tripCounts = getTripLegCounts(raw.trips);
    if (pickCount === null && tripCounts.pick > 0) {
      pickCount = tripCounts.pick;
    }
    if (dropCount === null && tripCounts.drop > 0) {
      dropCount = tripCounts.drop;
    }
  }

  const inferredTotalCount = getPassengerCountFromStandardFields(raw);

  if (inferredTotalCount !== null) {
    if (pickCount === null && inferredKind === 'pick') {
      pickCount = inferredTotalCount;
    }
    if (dropCount === null && inferredKind === 'drop') {
      dropCount = inferredTotalCount;
    }
  }

  if (pickCount !== null || dropCount !== null) {
    return (pickCount ?? 0) - (dropCount ?? 0);
  }

  if (inferredKind === 'pick') return 1;
  if (inferredKind === 'drop') return -1;
  return null;
}

/**
 * Infer per-stop boarding/alighting counts.
 * Returns null when the row has no passenger movement signal.
 */
export function inferActivityBoardingAndAlightingCounts(
  a: SimulationActivity
): { boarding: number; alighting: number } | null {
  const raw = a as Record<string, unknown>;
  const inferredKind = inferActivityStopKind(a);
  const pickCountFields = [
    'number_of_rides_picked_up',
    'pick_count',
    'pickup_count',
    'pickup_passengers_count',
    'picked_up_passengers_count',
    'passengers_picked_up_count',
    'number_of_passengers_picked_up',
  ];
  const dropCountFields = [
    'number_of_rides_dropped_off',
    'drop_count',
    'dropoff_count',
    'drop_off_count',
    'dropoff_passengers_count',
    'passengers_dropped_off_count',
    'number_of_passengers_dropped_off',
  ];

  let boarding = getCountFromCandidateFields(raw, pickCountFields);
  let alighting = getCountFromCandidateFields(raw, dropCountFields);

  if (boarding === null && Array.isArray(raw.users_picked_up)) {
    boarding = raw.users_picked_up.length;
  }
  if (alighting === null && Array.isArray(raw.users_dropped_off)) {
    alighting = raw.users_dropped_off.length;
  }

  if (boarding === null || alighting === null) {
    const tripCounts = getTripLegCounts(raw.trips);
    if (boarding === null && tripCounts.pick > 0) {
      boarding = tripCounts.pick;
    }
    if (alighting === null && tripCounts.drop > 0) {
      alighting = tripCounts.drop;
    }
  }

  const inferredTotalCount = getPassengerCountFromStandardFields(raw);
  if (inferredTotalCount !== null) {
    if (boarding === null && inferredKind === 'pick') boarding = inferredTotalCount;
    if (alighting === null && inferredKind === 'drop') alighting = inferredTotalCount;
  }

  if (boarding === null && alighting === null) {
    if (inferredKind === 'pick') return { boarding: 1, alighting: 0 };
    if (inferredKind === 'drop') return { boarding: 0, alighting: 1 };
    return null;
  }

  return {
    boarding: boarding ?? 0,
    alighting: alighting ?? 0,
  };
}

/**
 * Map inferred stop kind to map / panel labels used by simulation results UI.
 */
export function activityStopKindForUi(a: SimulationActivity): 'pickup' | 'dropoff' | 'other' {
  const k = inferActivityStopKind(a);
  if (k === 'pick') return 'pickup';
  if (k === 'drop') return 'dropoff';
  return 'other';
}
